/**
 * Order Status Modal Component
 * Modal for changing order status
 */

import React, { useEffect, useState } from 'react';
import { Form, Input, Modal, Select } from 'antd';
import { ORDER_STATUS_LABELS, OrderStatus } from '../../constants';
import { IDataverseOrder } from '../../types';
import { getOrderNumber } from '../../utils';

interface OrderStatusModalProps {
  open: boolean;
  order: IDataverseOrder | null;
  onCancel: () => void;
  onConfirm: (orderId: string, orderStatus: OrderStatus, evolutionJobNumber?: string) => void;
  loading?: boolean;
}

const opt = (s: OrderStatus) => ({ value: s, label: ORDER_STATUS_LABELS[s] });

const STATUS_TRANSITIONS: Partial<Record<OrderStatus, ReturnType<typeof opt>[]>> = {
  [OrderStatus.NEW]:                  [opt(OrderStatus.Cancelled)],
  [OrderStatus.PENDING]:              [opt(OrderStatus.Cancelled)],
  [OrderStatus.CREDIT_LIMIT_EXCEEDED]: [opt(OrderStatus.Cancelled)],
  [OrderStatus.AWAITING_PAYMENT]:    [opt(OrderStatus.Cancelled)],
  [OrderStatus.IN_PRODUCTION]:       [opt(OrderStatus.Completed), opt(OrderStatus.Cancelled)],
  [OrderStatus.READY_TO_PRODUCTION]: [opt(OrderStatus.IN_PRODUCTION), opt(OrderStatus.Cancelled)],
};

const EVOLUTION_JOB_NUMBER_RULES = [
  { required: true, message: 'Evolution job number is required' },
  { pattern: /^\d{6}$/, message: 'Evolution job number must be exactly 6 digits' },
];

export const OrderStatusModal: React.FC<OrderStatusModalProps> = ({
  open,
  order,
  onCancel,
  onConfirm,
  loading = false,
}) => {
  const [form] = Form.useForm<{ status?: OrderStatus; evolutionJobNumber?: string }>();
  const [selectedStatus, setSelectedStatus] = useState<OrderStatus | undefined>(undefined);

  useEffect(() => {
    if (open) {
      setSelectedStatus(undefined);
      form.resetFields();
    }
  }, [open, form]);

  const ORDER_STATUS_OPTIONS = order?.statuscode != null && order.statuscode in STATUS_TRANSITIONS
    ? STATUS_TRANSITIONS[order.statuscode as OrderStatus]!
    : [];

  const requiresEvolutionJobNumber = selectedStatus === OrderStatus.IN_PRODUCTION;

  const handleConfirm = async () => {
    if (!order) return;
    const values = await form.validateFields();
    if (!values.status) return;
    onConfirm(order.salesorderid, values.status, requiresEvolutionJobNumber ? values.evolutionJobNumber : undefined);
  };

  const handleCancel = () => {
    setSelectedStatus(undefined);
    form.resetFields();
    onCancel();
  };

  return (
    <Modal
      title="Change Order Status"
      open={open}
      onOk={handleConfirm}
      onCancel={handleCancel}
      confirmLoading={loading}
      okText="Update Status"
      cancelText="Cancel"
      width={500}
    >
      <div className="space-y-4 py-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Order Number
          </label>
          <div className="text-base font-semibold">{getOrderNumber(order) || 'N/A'}</div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Current Order Status
          </label>
          <div className="text-base">
            {order?.["statuscode@OData.Community.Display.V1.FormattedValue"] || 'Unknown'}
          </div>
        </div>

        <Form form={form} layout="vertical">
          <Form.Item
            label="New Order Status"
            name="status"
            rules={[{ required: true, message: 'Please select a new order status' }]}
          >
            <Select
              onChange={(value) => setSelectedStatus(value)}
              options={ORDER_STATUS_OPTIONS}
              size="middle"
              placeholder="Select order status"
            />
          </Form.Item>

          {requiresEvolutionJobNumber && (
            <Form.Item
              label="Evolution Job Number"
              name="evolutionJobNumber"
              rules={EVOLUTION_JOB_NUMBER_RULES}
            >
              <Input
                placeholder="Enter 6-digit Evolution job number"
                maxLength={6}
                inputMode="numeric"
                onKeyPress={(e) => {
                  if (!/[0-9]/.test(e.key)) e.preventDefault();
                }}
                onChange={(e) => {
                  const digitsOnly = e.target.value.replace(/\D/g, '').slice(0, 6);
                  form.setFieldValue('evolutionJobNumber', digitsOnly);
                }}
              />
            </Form.Item>
          )}
        </Form>
      </div>
    </Modal>
  );
};
